<template>
  <a-table
    class="stripped table-hover"
    :columns="columns"
    :data-source="data"
    :pagination="false"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'ticket_number'">
        <router-link
          :to="editLink(record)"
          class="profile-split d-flex flex-column hover-link text-wrap"
          style="white-space: normal; word-break: break-word;"
        >
          {{ record.ticket_number || '—' }}
        </router-link>
      </template>

      <template v-else-if="column.key === 'organization_name'">
        <span :class="{ 'text-danger': record.open_complaint === 'Yes' }">
          {{ record.organization_name || '—' }}
        </span>
        <span v-if="record.open_complaint === 'Yes'" class="badge bg-danger ms-1">Open complaint</span>
      </template>

      <template v-else-if="column.key === 'subject'">
        <router-link
          :to="editLink(record)"
          class="profile-split d-flex flex-column hover-link text-wrap"
          style="white-space: normal; word-break: break-word;"
        >
          {{ record.subject || '—' }}
        </router-link>
      </template>

      <template v-else-if="column.key === 'status'">
        <span
          v-if="statusKey(record.status) === 'open' || statusKey(record.status) === 'created'"
          class="px-2 py-1 rounded"
          style="background-color: #fbf7e1; color: #9E7328;"
        >{{ record.status }}</span>
        <span
          v-else-if="statusKey(record.status) === 'closed'"
          class="px-2 py-1 rounded"
          style="background-color: #dbf0de; color: #419b50;"
        >{{ record.status }}</span>
        <span v-else>{{ record.status || '—' }}</span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="icon-field">
          <router-link :to="editLink(record)" class="btn table_action_btn">
            <i class="ti ti-edit"></i> Edit
          </router-link>
        </div>
      </template>
    </template>
  </a-table>

  <div class="pagination-container" v-if="data.length > 0">
    <a-pagination
      :current="pagination.current"
      :pageSize="pagination.pageSize"
      :total="pagination.total"
      showSizeChanger
      :pageSizeOptions="pageSizeOptions"
      @change="handleTableChange"
      class="d-flex"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';

const columns = [
  {
    title: 'Case #',
    dataIndex: 'ticket_number',
    key: 'ticket_number',
    sorter: { compare: (a, b) => String(a.ticket_number || '').localeCompare(String(b.ticket_number || '')) },
  },
  {
    title: 'Organisation',
    dataIndex: 'organization_name',
    key: 'organization_name',
    sorter: { compare: (a, b) => String(a.organization_name || '').localeCompare(String(b.organization_name || '')) },
  },
  {
    title: 'Type',
    dataIndex: 'ticket_type',
    key: 'ticket_type',
    sorter: { compare: (a, b) => String(a.ticket_type || '').localeCompare(String(b.ticket_type || '')) },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: { compare: (a, b) => String(a.status || '').localeCompare(String(b.status || '')) },
  },
  {
    title: 'Subject',
    dataIndex: 'subject',
    key: 'subject',
    sorter: { compare: (a, b) => String(a.subject || '').localeCompare(String(b.subject || '')) },
  },
  {
    title: 'Deadline',
    dataIndex: 'deadline',
    key: 'deadline',
    sorter: {
      compare: (a, b) => new Date(a.deadline_raw || 0) - new Date(b.deadline_raw || 0),
    },
  },
  {
    title: 'Responsible',
    dataIndex: 'responsible_name',
    key: 'responsible_name',
    sorter: { compare: (a, b) => String(a.responsible_name || '').localeCompare(String(b.responsible_name || '')) },
  },
  {
    title: 'Action',
    dataIndex: 'Action',
    key: 'action',
  },
];

export default {
  props: {
    searchQuery: String,
    filters: Object,
  },
  setup(props) {
    const store = useStore();
    const data = ref([]);
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
    });

    const pageSizeOptions = computed(() => {
      const options = ['10'];
      if (pagination.value.total > 10) options.push('20');
      if (pagination.value.total > 20) options.push('50');
      if (pagination.value.total > 50) options.push('100');
      return options;
    });

    const editLink = (record) => ({
      path: '/crm/edit-org-support-case',
      query: {
        caseId: record.key,
        org_Id: record.organization_id,
        for: 'support-cases',
      },
    });

    const statusKey = (status) => String(status || '').trim().toLowerCase();

    const fetchCases = async (page) => {
      if (typeof page === 'number' && Number.isFinite(page)) {
        pagination.value.current = page;
      }
      const params = {
        search: props.searchQuery || undefined,
        page: pagination.value.current,
        pageSize: pagination.value.pageSize,
        status: props.filters?.status || undefined,
        ticket_type: props.filters?.type || undefined,
      };
      const response = await store.dispatch('orgSupportCases/fetchOrgSupportCases', params);
      if (response) {
        const rows = Array.isArray(response.data) ? response.data : [];
        data.value = rows.map((res) => ({
          key: res.id,
          organization_id: res.organization_id,
          ticket_number: res.ticket_number || '—',
          organization_name: res.organization_name || '—',
          open_complaint: res.open_complaint || '',
          ticket_type: res.ticket_type || '—',
          status: res.status || '—',
          subject: res.subject || '—',
          deadline: res.deadline ? moment(res.deadline).format('DD/MM/YYYY HH:mm') : '—',
          deadline_raw: res.deadline,
          responsible_name: res.responsible_name || '—',
        }));
        pagination.value.total = Number(response.total || 0);
      }
    };

    const handleTableChange = (current, pageSize) => {
      pagination.value.current = current;
      pagination.value.pageSize = pageSize;
      fetchCases();
    };

    onMounted(() => fetchCases());

    return {
      columns,
      data,
      pagination,
      pageSizeOptions,
      fetchCases,
      handleTableChange,
      editLink,
      statusKey,
    };
  },
};
</script>

<style scoped>
.hover-link {
  color: #6092d0;
  font-weight: 600;
}
.hover-link:hover {
  text-decoration: underline;
  transition: all 0.2s ease;
}
</style>
